import { isAuthBypassMode } from "@/lib/auth-health";
import { useEffect, useState } from "react";

type Health = {
  environment?: string;
  auth_mode?: string;
};

const BANNER_KEY = "mx.banner";
const MESSAGE_ID = "dev-auth-banner-message";

/**
 * Honest staging/local marker. Shown when /api/health reports
 * auth_mode=auth_bypass (or leftover cookie_demo). Must not resemble
 * production chrome or claim SSO / Better Auth.
 */
export function DevAuthBanner() {
  const [health, setHealth] = useState<Health | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(BANNER_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [details, setDetails] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetch("/api/health", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: Health | null) => {
        if (!cancelled && body) setHealth(body);
      })
      .catch(() => {
        /* worker down — no banner */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (dismissed) return null;
  const bypass = isAuthBypassMode(health);
  const cookieDemo = health?.auth_mode === "cookie_demo";
  if (!bypass && !cookieDemo) return null;
  const staging = health?.environment === "staging";
  const local = health?.environment === "development";
  if (!bypass && !staging && !local) return null;

  const short = bypass
    ? staging
      ? "Staging — auth bypass / no login"
      : "Local — auth bypass / no login"
    : staging
      ? "staging dogfood · cookie demo-auth — not production SSO"
      : "demo-auth · Think unbound — not production SSO";
  const long = bypass
    ? staging
      ? "Staging workers.dev — auth bypass / no login. Not Better Auth. Not production SSO. Not pcbx.ai."
      : "Local development — auth bypass / no login. Not Better Auth. Not production SSO."
    : staging
      ? "workers.dev staging — cookie demo-auth (pcbx_dev_user). AUTH_DB unbound. Not Better Auth. Not production. Not pcbx.ai."
      : "Local development — cookie demo-auth (pcbx_dev_user). Not Better Auth. Not production. Think DO / AUTH_DB unbound.";

  return (
    <div className={details ? "dev-auth-banner dev-auth-banner-open" : "dev-auth-banner"} role="status">
      <span className="dev-auth-banner-mark">{staging ? "Staging" : local ? "Local" : "Bypass"}</span>
      <span id={MESSAGE_ID} className={details ? "dev-auth-banner-message" : "dev-auth-banner-message truncate"}>
        {details ? long : short}
      </span>
      <div className="dev-auth-banner-actions">
        <button
          type="button"
          className="dev-auth-banner-ghost"
          aria-expanded={details}
          aria-controls={MESSAGE_ID}
          onClick={() => setDetails((v) => !v)}
        >
          {details ? "Hide" : "Details"}
        </button>
        <button
          type="button"
          className="dev-auth-banner-ghost"
          onClick={() => {
            try {
              sessionStorage.setItem(BANNER_KEY, "1");
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
